<template>
  <div class="profit-trend">
    <div class="profit-trend-header">
      <div class="profit-trend-header__label">
        {{ $t('calculator.profit-trend.profit-label') }}
      </div>
      <div class="profit-trend-header__profit">
        <slot name="profit" />
      </div>
    </div>
    <div ref="container" class="profit-trend-graph">
      <canvas ref="chart"></canvas>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { ref, watch } from "vue";
import { useIntersectionObserver } from '@vueuse/core'
import { ProfitCalculatorChartData, ProfitCalculatorCurrency } from "@kano/types/profit-calculator";
import { formatNumberStandard } from "@kano/helpers/numberFormatter";
import { i18n } from "@kano/i18n";

const props = withDefaults(defineProps<{
  chartData: Array<ProfitCalculatorChartData>,
  fiatCurrency: ProfitCalculatorCurrency,
}>(), {
  chartData: [],
});

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
);

const container = ref<HTMLDivElement|null>(null);
const chart = ref<HTMLCanvasElement|null>(null);

let chartObject = null;

const { stop } = useIntersectionObserver(
  container,
  ([{ isIntersecting }]) => {
    if (isIntersecting && chart.value) {
      chartObject = createChart(props.chartData, chart.value);
      stop();
    }
  },
  { threshold: 0.5 },
);

watch(() => props.chartData, (value) => {
  if (!value.length || !chart.value || !chartObject) {
    return;
  }

  chartObject.data.datasets[0].data = value;
  chartObject.update();
});

const createChart = (chartData, chartElement) => {
  return new Chart(chartElement, {
    type: 'line',
    data: {
      labels: false,
      datasets: [{
        backgroundColor: ({ chart }) => {
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            // This case happens on initial chart load
            return;
          }
          const gradientFill = ctx.createLinearGradient(0, 0, 0, chartArea.height);
          gradientFill.addColorStop(0, '#d5d6f2');
          gradientFill.addColorStop(1, '#f1f2fa');

          return gradientFill;
        },
        data: chartData,
        pointStyle: false,
        borderColor: '#5F70DB',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
        fill: true,
        clip: false,
        segment: {
          borderDash: ({ p0 }) => {
            return new Date() < new Date(p0.raw.date) ? [8, 4] : undefined
          },
        },
      }],
    },
    options: {
      layout: {
        padding: {
          bottom: 40,
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          usePointStyle: true,
          axis: 'x',
          callbacks: {
            title: (tooltipItemList) => chartMethods.formatStringToLongDate(tooltipItemList[0].label),
            beforeBody: (tooltipItemList) => chartMethods.formatAmountString(tooltipItemList[0].parsed.y),
            label: () => '',
          },
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 20,
          },
        },
      },
      parsing: {
        xAxisKey: 'date',
        yAxisKey: 'predictedValue',
      },
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      scales: {
        y: {
          border: {
            display: false,
            dash: [6, 4]
          },
          grid: {
            display: true,
            drawTicks: false,
            z: 1,
          },
          ticks: {
            align: 'end',
            padding: 10,
            maxTicksLimit: 5,
            callback: function (val) {
              return chartMethods.formatNumberToShort(Number(val));
            }
          },
        },
        x: {
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
          ticks: {
            align: 'start',
            padding: 10,
            maxTicksLimit: 4,
            callback: function (val) {
              const label = this.getLabelForValue(val);
              return chartMethods.formatStringToShortDate(label);
            },
          },
        },
      },
      resizeDelay: 100,
    },
  });
}

const chartMethods = {
  formatAmountString (amountString: string) {
    return formatNumberStandard(Number(amountString), i18n.locale, props.fiatCurrency.code);
  },
  formatStringToLongDate (dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },
  formatStringToShortDate (dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'numeric',
    });
  },
  formatNumberToShort (number: number) {
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(number);
  }
};

</script>

<style scoped lang="scss">
@use '@kanoAssets/styles/breakpoints.scss';

.profit-trend {
  display: flex;
  flex-direction: column;
  align-content: space-between;
  height: 100%;
  justify-content: space-between;
}

.profit-trend-header {
  text-align: center;
  padding-bottom: 40px;

  &__label {
    font-size: 14px;
    color: var(--color-base-64);
  }

  @include breakpoints.desktop {
    padding: 24px 24px 48px;
    text-align: start;
  }
}

.profit-trend-graph {
  height: 429px;
}
</style>